import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Form, Button, Alert } from "react-bootstrap";
import api from "../common/api"; 
import IconsBackground from "./IconsBackground";
import useTenantAssets from "../common/useTenantLogo";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "../App.css";

  const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const { getLogo } = useTenantAssets();
  const [stage, setStage] = useState(token ? 3 : 1);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const intervalRef = useRef(null);

  const isPassValidate = (pass) => {
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/.test(pass);
};

  const handleSendLink = async () => {
    if (!email) return setError("Please enter your email.");
    setLoading(true);
    setError("");
    setMessage("");
    try {
      await api.post("/forgotpassword", { email });
      setMessage("A password reset link has been sent to your email.");
      setStage(2);
      startTime(); // start timer
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError("Email not found. Please check and try again.");
      } else {
        setError("Something went wrong. Please try again or later.");
      }
    }
    setLoading(false);
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) return setError("Please fill in all fields.");
    if (newPassword !== confirmPassword) return setError("Passwords do not match.");
    if (!isPassValidate(newPassword)) return setError("Please enter a strong password.");
    setLoading(true);
    setError("");
    try {
      await api.post("/resetpassword", { token, newPassword,confirmPassword });
      setMessage(
      "Password changed successfully! This link cannot be used again. " +
      "If needed, request a new reset link."
    );
      setStage(4);
    } catch (err) {
      setError("Token expired or same as old password not allowed.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (stage === 4) {
        const timer = setTimeout(() => {navigate("/login", { state: { fromReset: true,message: "Password changed successfully! Please log in with your new password." } }); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [stage, navigate]);

  const startTime = () => {
    setTime(30);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setTime((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // cleanup
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);


  return (
    <div style={{ minHeight: "100vh", width: "100%", position: "relative", backgroundColor: "var(--primary)", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Card className="forgetPasswordCard">

        {stage === 1 && (
          <>
            <span style={{display:"flex", justifyContent:"center", alignItems:"center", gap:"15px"}}> 
              <img src={getLogo("login")} alt="" style={{width:"80px"}} />
              <h2 className="forgetPasswordHeader">Forgot Password ?</h2>
            </span>
            <p className="forgetPasswordDescription">Enter your registered email to reset password</p>
            {error && <Alert variant="danger">{error}</Alert>}
            {message && <Alert variant="success">{message}</Alert>}
            <Form>
              <Form.Group className="mb-3">
                <Form.Control type="email" placeholder="Email" value={email} className="forgetPasswordInput" onChange={(e) => setEmail(e.target.value)}/>
              </Form.Group>
              <Button className="forgetPasswordButton" onClick={handleSendLink} disabled={loading}>
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
              <p className="mt-3" style={{ fontSize: "14px" }}>
                Remember your password? <a href="/login" style={{ color: "var(--primary)" }}>Login</a>
              </p>
            </Form>
          </>
        )}

        {stage === 2 && (
          <>
            <h2 className="forgetPasswordHeader">Check Your Email</h2>
            <p className="forgetPasswordDescription">
              We’ve sent a password reset link to <strong>{email}</strong>.
            </p>

            <p style={{ fontSize: "14px", color: "#666" }}>
              {loading ? "" : time > 0 ? `${time} seconds remaining` : "You can resend now"}
            </p>

            {error && <Alert variant="danger">{error}</Alert>}

            <Button
              className="forgetPasswordButton"
              onClick={handleSendLink}
              disabled={loading || time > 0}
            >
              {loading ? "Sending..." : time > 0 ? `Resend Email` : "Resend Email"}
            </Button>
          </>
        )}

       {stage === 3 && (
  <>
    <h2 className="forgetPasswordHeader">Reset Password</h2>
    <p className="descriptionStyle">Enter your new password below.</p>
    {error && <Alert variant="danger">{error}</Alert>}

    <Form>
      <Form.Group className="mb-3">
        <div style={{ position: "relative" }}>
          <Form.Control
            type={showPassword ? "text" : "password"}
            placeholder="New Password"
            value={newPassword}
            className="forgetPasswordInput"
            onChange={(e) => setNewPassword(e.target.value)}
          />

          <span
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
      </Form.Group>

      <Form.Group className="mb-3">
        <div style={{ position: "relative" }}>
          <Form.Control
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={confirmPassword}
            className="forgetPasswordInput"
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <span
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
          >
            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
        </div>
      </Form.Group>

              {error === "Please enter a strong password." && (
        <p style={{ fontSize: "13px", color: "red", textAlign: "left", marginBottom: "10px" }}>
          Password must contain:
          <br />• At least 8 characters
          <br />• One uppercase letter (A-Z)
          <br />• One lowercase letter (a-z)
          <br />• One number (0-9)
          <br />• One special character (@$!%*?&)
        </p>
      )}
      <Button
        className="forgetPasswordButton"
        onClick={handleResetPassword}
        disabled={loading}
      >
        {loading ? "Updating..." : "Update Password"}
      </Button>
    </Form>
  </>
)}

        {stage === 4 && (
          <>
            <h2 className="forgetPasswordHeader">Success!</h2>
            {message && <Alert variant="success">{message}</Alert>}
            <Button className="forgetPasswordButton" onClick={() => navigate("/login")}>
              Redirecting To login - Go to Login
            </Button>
          </>
        )}

      </Card>
    </div>
  );
};

export default ForgotPasswordPage;